import { useEffect, useState } from "react";
import api from "../utils/axios";
import Swal from "sweetalert2";
import formatCurrency from "../helper/formatCurrency";

interface DayReward {
  id?: number;
  day_number: number;
  reward_amount: number | string;
  min_deposit: number | string;
  is_active: boolean;
}

interface Stats {
  total_bonus_given: string;
  total_claims_count: string;
  total_unique_users: string;
}

const DailySignInSettings = () => {
  const [rewards, setRewards] = useState<DayReward[]>([]);
  const [minDeposit, setMinDeposit] = useState<string>("100.00");
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get("/daily-signin-settings");
      if (res.data?.status) {
        setRewards(res.data.data || []);
        if (res.data.data?.length) {
          setMinDeposit(res.data.data[0].min_deposit || "100.00");
        }
        setStats(res.data.stats || null);
      }
    } catch (err) {
      console.error("Failed to load daily sign-in settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleRewardChange = (index: number, field: keyof DayReward, value: any) => {
    const updated = [...rewards];
    updated[index] = { ...updated[index], [field]: value };
    setRewards(updated);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/daily-signin-settings", {
        rewards,
        min_deposit: minDeposit,
      });

      if (res.data?.status) {
        Swal.fire({
          icon: "success",
          title: "Settings Saved",
          text: "Daily sign-in rewards & rules updated successfully!",
        });
        fetchSettings();
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: err?.response?.data?.message || "Failed to update daily sign-in settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Daily Sign-in Bonus Settings</h1>
        <p className="text-sm text-gray-500">
          Configure Day 1 to Day 7 streak reward amounts and minimum deposit requirements.
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Total Bonus Distributed</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">
              {formatCurrency(stats.total_bonus_given)}
            </p>
          </div>
          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Total Claims</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_claims_count}</p>
          </div>
          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Unique Members Claimed</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{stats.total_unique_users}</p>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSave} className="space-y-6">
        {/* Global Rule */}
        <div className="bg-white border rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Deposit Growth Plan Requirement</h2>
          <p className="text-xs text-gray-500 mb-4">
            Minimum deposit required for members to qualify and unlock daily sign-in rewards.
          </p>

          <div className="max-w-xs">
            <label className="text-xs font-semibold text-gray-600">Minimum Deposit (৳)</label>
            <input
              type="number"
              value={minDeposit}
              onChange={(e) => setMinDeposit(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mt-1 text-sm font-medium"
              placeholder="100.00"
            />
          </div>
        </div>

        {/* 7-Day Rewards Table */}
        <div className="bg-white border rounded-2xl shadow-sm overflow-hidden p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">7-Day Streak Rewards Configuration</h2>

          {loading ? (
            <p className="py-8 text-center text-gray-500">Loading settings...</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 gap-4">
              {rewards.map((r, idx) => (
                <div
                  key={r.day_number}
                  className={`border-2 rounded-xl p-4 flex flex-col justify-between ${
                    r.day_number === 7
                      ? "border-amber-400 bg-amber-50/40"
                      : "border-gray-200 bg-gray-50/50"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-sm text-gray-800">
                      Day {r.day_number} {r.day_number === 7 && "🎁"}
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={r.is_active}
                        onChange={(e) =>
                          handleRewardChange(idx, "is_active", e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-emerald-600"></div>
                    </label>
                  </div>

                  <div>
                    <label className="text-[11px] font-medium text-gray-500">Reward (৳)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={r.reward_amount}
                      onChange={(e) =>
                        handleRewardChange(idx, "reward_amount", e.target.value)
                      }
                      className="w-full border rounded-lg px-2.5 py-1.5 mt-1 text-sm font-bold text-emerald-700 bg-white"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 disabled:opacity-50 transition"
          >
            {saving ? "Saving Changes..." : "Save Daily Sign-in Settings"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DailySignInSettings;
